import { Controller } from "@hotwired/stimulus"
import { EditorState } from "@codemirror/state"
import { EditorView, keymap } from "@codemirror/view"
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands"
import { autocompletion, completionKeymap, closeBrackets } from "@codemirror/autocomplete"
import { bracketMatching } from "@codemirror/language"
import { oneDark } from "@codemirror/theme-one-dark"
import { esqlLanguage, esqlSyntaxHighlighting } from "../../../src/esql_language"

// ES|QL keywords and functions — only what the backend actually supports
const KEYWORDS = [
  "FROM", "WHERE", "KEEP", "DROP", "STATS", "SORT", "LIMIT", "RENAME",
  "CORRELATE",
  "AND", "OR", "NOT", "IS", "NULL",
  "BETWEEN", "NOT BETWEEN",
  "LIKE", "NOT LIKE", "RLIKE", "NOT RLIKE",
  "IN", "NOT IN",
  "BY", "AS", "ASC", "DESC",
  "| render timechart", "| render barchart", "| render areachart", "| render piechart", "| render scatterchart",
].map(label => ({ label, type: "keyword" }))

const FUNCTIONS = [
  // Aggregations (for STATS)
  "COUNT", "COUNT_DISTINCT", "AVG", "MAX", "MIN", "SUM", "PERCENTILE",
  // String scalar functions (for WHERE)
  "TO_LOWER", "TO_UPPER", "STARTS_WITH", "ENDS_WITH", "CONTAINS",
  // IP function (for WHERE)
  "CIDR_MATCH",
  // Full-text match (for WHERE)
  "MATCH",
  // Math functions (for WHERE)
  "ABS", "ROUND", "FLOOR", "CEIL", "LENGTH",
  // Date functions (for STATS BY)
  "DATE_TRUNC",
].map(label => ({ label: `${label}()`, type: "function", apply: label + "(" }))

const ESQL_COMPLETIONS = [...KEYWORDS, ...FUNCTIONS]

// Detects if the cursor is positioned where a field value should be suggested.
// Matches: field == "partial  or  field LIKE "partial  or  field IN ("partial
const VALUE_CONTEXT_RE = /(\w[\w.]*)\s*(?:==|!=|like|rlike|not\s+like|not\s+rlike)\s*"([^"]*)$/i
const IN_CONTEXT_RE = /(\w[\w.]*)\s+(?:not\s+)?in\s*\([^)]*"([^"]*)$/i

export default class extends Controller {
  static values = { fieldValuesUrl: String }

  #view = null
  #textarea = null
  #syncing = false
  #boundSync = null

  connect() {
    let cachedFields = []
    let fieldValuesBaseUrl = this.hasFieldValuesUrlValue ? this.fieldValuesUrlValue : ""

    const loadFieldsFromDOM = () => {
      const fields = Array.from(document.querySelectorAll("[data-field-name]"))
        .map(el => el.dataset.fieldName)
        .filter(Boolean)
      if (fields.length) cachedFields = [...new Set(fields)]
    }

    const valueContext = (context) => {
      if (!fieldValuesBaseUrl) return null
      const line = context.state.doc.lineAt(context.pos)
      const before = line.text.slice(0, context.pos - line.from)
      const match = before.match(VALUE_CONTEXT_RE) || before.match(IN_CONTEXT_RE)
      if (!match) return null

      const partial = match[2]
      const from = context.pos - partial.length

      return {
        from,
        options: [],
        validFor: /^[^"]*$/,
        update(view, startState, updates) { return null },
      }
    }

    const esqlComplete = (context) => {
      const valueSuggest = valueContext(context)
      if (valueSuggest) return valueSuggest

      const word = context.matchBefore(/[\w.]*/)
      if (!word || (word.from === word.to && !context.explicit)) return null

      const text = word.text.toLowerCase()
      const completions = [
        ...ESQL_COMPLETIONS,
        ...cachedFields.map(f => ({ label: f, type: "variable" })),
      ].filter(c => c.label.toLowerCase().startsWith(text))

      return { from: word.from, options: completions }
    }

    const fieldValueComplete = async (context) => {
      if (!fieldValuesBaseUrl) return null
      const line = context.state.doc.lineAt(context.pos)
      const before = line.text.slice(0, context.pos - line.from)
      const match = before.match(VALUE_CONTEXT_RE) || before.match(IN_CONTEXT_RE)
      if (!match) return null

      const fieldName = match[1]
      const partial = match[2]
      const from = context.pos - partial.length

      try {
        const url = new URL(fieldValuesBaseUrl, location.origin)
        url.searchParams.set("field", fieldName)
        const resp = await fetch(url.toString(), { headers: { Accept: "application/json" } })
        if (!resp.ok) return null
        const values = await resp.json()
        const options = values
          .filter(v => String(v).toLowerCase().startsWith(partial.toLowerCase()))
          .map(v => ({ label: String(v), type: "value", apply: String(v) }))
        return options.length ? { from, options, validFor: /^[^"]*$/ } : null
      } catch {
        return null
      }
    }

    loadFieldsFromDOM()

    this.#textarea = this.element

    const isDark = document.body.classList.contains("dark")
    const extensions = [
      history(),
      bracketMatching(),
      closeBrackets(),
      esqlLanguage,
      esqlSyntaxHighlighting,
      autocompletion({ override: [fieldValueComplete, esqlComplete] }),
      keymap.of([
        ...defaultKeymap,
        ...historyKeymap,
        ...completionKeymap,
        { key: "Ctrl-Enter", run: () => { this.#submit(); return true } },
        { key: "Mod-Enter", run: () => { this.#submit(); return true } },
      ]),
      EditorView.updateListener.of(update => {
        if (update.docChanged && !this.#syncing) {
          this.#syncToTextarea(update.state.doc.toString())
        }
      }),
      EditorView.domEventHandlers({
        focus: () => this.#textarea.dispatchEvent(new Event("focus", { bubbles: true })),
        blur: () => this.#textarea.dispatchEvent(new Event("blur", { bubbles: true })),
      }),
      EditorView.theme({
        "&": { minHeight: "36px", maxHeight: "200px" },
        ".cm-scroller": { overflow: "auto", fontFamily: "inherit", fontSize: "14px" },
        ".cm-content": { padding: "6px 8px", whiteSpace: "pre-wrap", wordBreak: "break-word" },
        ".cm-focused": { outline: "none" },
        ".cm-line": { lineHeight: "1.5" },
      }),
      ...(isDark ? [oneDark] : []),
    ]

    const state = EditorState.create({
      doc: this.#textarea.value,
      extensions,
    })

    this.#view = new EditorView({ state, parent: this.#textarea.parentElement })
    this.#view.dom.classList.add("siem-codemirror")

    // Insert the CodeMirror editor before the hidden textarea
    this.#textarea.parentElement.insertBefore(this.#view.dom, this.#textarea)
    this.#textarea.style.display = "none"

    // Sync external changes (query history, field browser inserts, etc.)
    this.#boundSync = () => {
      if (this.#syncing) return
      const newVal = this.#textarea.value
      const cmVal = this.#view?.state.doc.toString()
      if (newVal !== cmVal) {
        this.#syncing = true
        this.#view?.dispatch({
          changes: { from: 0, to: this.#view.state.doc.length, insert: newVal },
        })
        this.#syncing = false
      }
    }
    this.#textarea.addEventListener("input", this.#boundSync)
  }

  disconnect() {
    this.#textarea?.removeEventListener("input", this.#boundSync)
    this.#view?.destroy()
    if (this.#textarea) this.#textarea.style.display = ""
  }

  #syncToTextarea(value) {
    this.#syncing = true
    this.#textarea.value = value
    this.#textarea.dispatchEvent(new Event("input", { bubbles: true }))
    this.#syncing = false
  }

  setValue(value, { cursorIndex = null, focus = false } = {}) {
    if (!this.#view) {
      this.#textarea.value = value
      this.#textarea.dispatchEvent(new Event("input", { bubbles: true }))
      if (focus) {
        this.#textarea.focus()
        const pos = cursorIndex ?? value.length
        this.#textarea.setSelectionRange(pos, pos)
      }
      return
    }

    this.#syncing = true
    try {
      const transaction = {
        changes: { from: 0, to: this.#view.state.doc.length, insert: value },
      }
      if (cursorIndex != null) transaction.selection = { anchor: cursorIndex }
      this.#view.dispatch(transaction)
      this.#textarea.value = value
      this.#textarea.dispatchEvent(new Event("input", { bubbles: true }))
    } finally {
      this.#syncing = false
    }
    if (focus) this.#view.focus()
  }

  #submit() {
    const form = this.#textarea?.closest("form")
    if (form) form.requestSubmit()
  }
}
